import { useQuery } from '@apollo/client'
import React from 'react'
import styled from 'styled-components'

import Chart from 'pared/charts'
import { ILocationInfo } from 'pared/components/LocationInfo/hooks/useLocationInfo'
import Spin from 'pared/components/basicUi/spin'
import { MOBILE_WIDTH } from 'pared/constants/styles'
import {
  GROUP_BY_DAY,
  GROUP_BY_PERIOD,
  GROUP_BY_QUARTER,
  GROUP_BY_WEEK,
  IDateRange,
  TYPE_PERIOD,
  TYPE_QUARTER,
  TYPE_YEAR,
} from 'pared/data/getDateRanges'

import {
  IListLocationVsSystemwideAcrScoreType,
  IListLocationVsSystemwideAcrScoreVariablesType,
  listLocationVsSystemwideAcrScore,
} from './gql'
import useChartConfig from './hooks/useChartConfig'

interface IProps {
  selectedLocation: ILocationInfo
  selectedDateRange: IDateRange | null
}

const ChartContainer = styled.div`
  :not(:first-child) {
    margin: 40px 0px 0px;

    @media ${MOBILE_WIDTH} {
      margin: 20px 0px 0px;
    }
  }

  @media ${MOBILE_WIDTH} {
    padding: 20px 30px;
    border: 1px solid;
    overflow: scroll hidden;
  }
`

const SubHeader = styled.div`
  font-family: Lexend-SemiBold;
  font-size: 22px;
  font-style: normal;
  font-weight: 700;
  margin-bottom: 20px;
`

const AcrChart = ({ selectedLocation, selectedDateRange }: IProps) => {
  let groupBy = GROUP_BY_DAY
  let chartDataType = 'days'
  if (selectedDateRange?.type === TYPE_YEAR) {
    groupBy = GROUP_BY_QUARTER
    chartDataType = TYPE_YEAR
  } else if (selectedDateRange?.type === TYPE_QUARTER) {
    groupBy = GROUP_BY_PERIOD
    chartDataType = TYPE_QUARTER
  } else if (selectedDateRange?.type === TYPE_PERIOD) {
    groupBy = GROUP_BY_WEEK
    chartDataType = TYPE_PERIOD
  }

  const startDate = selectedDateRange?.startDate?.format('YYYY-MM-DD')
  const endDate = selectedDateRange?.endDate?.format('YYYY-MM-DD')

  const { data, loading } = useQuery<
    IListLocationVsSystemwideAcrScoreType,
    IListLocationVsSystemwideAcrScoreVariablesType
  >(listLocationVsSystemwideAcrScore, {
    variables: {
      iLocationId: selectedLocation.id,
      iStartDate: startDate || '',
      iEndDate: endDate || '',
      iGroupBy: groupBy,
    },
    skip: !selectedLocation || !startDate || !endDate || !groupBy,
  })

  const chartConfig = useChartConfig(
    data?.listLocationVsSystemwideAcrScore.nodes || [],
    chartDataType as
      | typeof TYPE_YEAR
      | typeof TYPE_QUARTER
      | typeof TYPE_PERIOD
      | 'days',
    selectedLocation.name,
  )

  return (
    <>
      <SubHeader>ACR Score</SubHeader>

      <ChartContainer>
        <Spin spinning={loading}>
          <Chart {...chartConfig} width={1100} height={350} />
        </Spin>
      </ChartContainer>
    </>
  )
}

export default React.memo(AcrChart)
